import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowBackOutlined } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { ResourcePage, ResourceCollection, ValueBadge, type ResourceCollectionState } from '../../../design-system';
import type { BulkOperationsPort } from '../api/port';
import { BulkPasteDialog } from '../components/BulkPasteDialog';
import { BulkStatus } from '../components/BulkOperationResults';
import { assertBulkOperationValid } from '../model/applyValidation';
import { getFixtureBulkOperation, listFixtureBulkOperations, saveFixtureBulkOperation } from '../fixtures/bulkOperationStore';
import type { BulkOperationDetail, BulkOperationJob, BulkValidatedRow } from '../model/bulkOperations';
import { bulkWorkflows } from '../model/workflows';
import { bulkOperationLabel, contextualBulkWorkflows, isBulkJobActive } from '../model/presentation';
import type { BulkReferencePort } from '../model/references';
import { createFixtureBulkReferencePort } from '../fixtures/bulkReferences';
import type { BulkOperationsRoutePort } from '../routing/port';
import { BulkOperationDetailDialog } from '../components/BulkOperationDetailDialog';

const sourceOperations = {
  people: ['EMPLOYEE_HIRE', 'EMPLOYEE_IMPORT', 'EMPLOYEE_REHIRE', 'STAFFING_CHANGE', 'WORKER_LIFECYCLE_CORRECTION'],
  staffing: ['STAFFING_CHANGE', 'WORKER_LIFECYCLE_CORRECTION'],
};

export type BulkOperationsPageProps = {
  port: BulkOperationsPort;
  referencePort?: BulkReferencePort;
  route: BulkOperationsRoutePort;
  fixtureMode: boolean;
  canCreate: boolean;
  canRun: boolean;
  canReadStaffing?: boolean;
  allowedOperationTypes?: readonly string[];
};

export function BulkOperationsPage({ port, referencePort, route, fixtureMode, canCreate, canRun, canReadStaffing = false, allowedOperationTypes }: BulkOperationsPageProps) {
  const context = useMemo(() => ({ port, fixtureMode, route }), [port, fixtureMode, route]);
  const currentContext = useRef(context);
  currentContext.current = context;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const capabilities = useRef({ canCreate, canRun, allowedOperationTypes });
  capabilities.current = { canCreate, canRun, allowedOperationTypes };
  const [location, setLocation] = useState(route.read);
  useEffect(() => {
    const sync = () => setLocation(route.read());
    sync();
    return route.subscribe(sync);
  }, [route]);
  const references = useMemo(() => fixtureMode ? createFixtureBulkReferencePort() : referencePort, [fixtureMode, referencePort]);
  const [resource, setResource] = useState<{ context: typeof context; data?: BulkOperationJob[]; loading: boolean; error?: string }>({ context, loading: true });
  const resourceData = resource.context === context ? resource.data : undefined;
  const resourceError = resource.context === context ? resource.error : undefined;
  const resourceLoading = resource.context !== context || resource.loading;
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((value) => value + 1), []);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const applying = useRef(false);
  const operationAvailable = (operation?: string) => !operation || allowedOperationTypes === undefined || allowedOperationTypes.includes(operation);
  const requestedOperation = location.operation && bulkWorkflows.some((workflow) => workflow.operationType === location.operation) ? location.operation : undefined;
  const dialogOpen = Boolean(requestedOperation && canCreate && canRun && operationAvailable(requestedOperation));
  const dialogWorkflows = requestedOperation
    ? contextualBulkWorkflows(requestedOperation, allowedOperationTypes ?? bulkWorkflows.map((workflow) => workflow.operationType)) : [];

  useEffect(() => {
    if (!location.active) return;
    let active = true;
    let timer: number | undefined;
    const load = async () => {
      setResource((value) => ({ context, data: value.context === context ? value.data : undefined, loading: true }));
      try {
        const data = fixtureMode ? listFixtureBulkOperations() : await port.listBulkOperations();
        if (!active || currentContext.current !== context) return;
        setResource({ context, data, loading: false });
        if (!fixtureMode && data.some((job) => isBulkJobActive(job.status))) timer = window.setTimeout(() => void load(), 2_000);
      } catch (cause) {
        if (active && currentContext.current === context) setResource({ context, loading: false, error: cause instanceof Error ? cause.message : '처리 내역을 불러오지 못했습니다.' });
      }
    };
    void load();
    return () => { active = false; window.clearTimeout(timer); };
  }, [context, port, fixtureMode, location.active, location.jobId, version]);

  useEffect(() => {
    if (location.active && requestedOperation && (!canCreate || !canRun || (allowedOperationTypes && !allowedOperationTypes.includes(requestedOperation)))) route.clearOperation();
  }, [route, location.active, requestedOperation, canCreate, canRun, allowedOperationTypes]);

  const sourceJobs = (resourceData ?? []).filter((job) => !location.source || sourceOperations[location.source].includes(job.operationType));
  const rows = sourceJobs.filter((row) => (!status || row.status === status)
    && `${bulkOperationLabel(row.operationType)} ${row.originalFilename ?? ''}`.toLocaleLowerCase().includes(query.toLocaleLowerCase()));
  const columns: GridColDef<BulkOperationJob>[] = [
    { field: 'operationType', headerName: '작업', width: 230, valueFormatter: (value) => bulkOperationLabel(String(value)), renderCell: ({ value }) => <ValueBadge label={bulkOperationLabel(value)} /> },
    { field: 'originalFilename', headerName: '입력', flex: 1, minWidth: 220 },
    { field: 'totalRows', headerName: '전체', width: 80, type: 'number' },
    { field: 'succeededRows', headerName: '제출 완료', width: 100, type: 'number' },
    { field: 'failedRows', headerName: '실패', width: 80, type: 'number' },
    { field: 'status', headerName: '상태', width: 130, renderCell: ({ value }) => <BulkStatus value={value} /> },
  ];
  const apply = async (operationType: string, inputRows: BulkValidatedRow[], idempotencyKey: string) => {
    const canApply = () => capabilities.current.canCreate && capabilities.current.canRun
      && (capabilities.current.allowedOperationTypes === undefined || capabilities.current.allowedOperationTypes.includes(operationType));
    if (!mounted.current || applying.current || !canApply()) throw new Error('현재 사용할 수 없는 기능입니다. 권한을 확인해 주세요.');
    const origin = route.read();
    const assertCurrent = () => {
      const current = route.read();
      if (!mounted.current || currentContext.current !== context || !current.active
        || current.jobId !== origin.jobId || current.operation !== origin.operation || current.source !== origin.source) {
        throw new Error('작업 화면이 변경되었습니다. 처리 내역에서 결과를 확인해 주세요.');
      }
      if (!canApply()) throw new Error('권한이 변경되었습니다. 처리 내역을 다시 확인해 주세요.');
    };
    assertCurrent();
    applying.current = true;
    try {
      const values = inputRows.map((row) => {
        const input = { ...row.values };
        if (operationType === 'TIME_OFF_CORRECTION') delete input.workerId;
        return input;
      });
      const fixtureId = `fixture-${idempotencyKey}`;
      const created: BulkOperationDetail = fixtureMode ? getFixtureBulkOperation(fixtureId) ?? {
        id: fixtureId, operationType, status: 'VALIDATED', originalFilename: `붙여넣기-${new Date().toISOString().slice(0, 10)}.tsv`,
        totalRows: values.length, validRows: values.length, succeededRows: 0, failedRows: 0, createdAt: new Date().toISOString(),
        rows: inputRows.map((_, index) => ({ rowNumber: index + 1, status: 'VALID' })),
      } : await port.createBulkOperation(operationType, `붙여넣기-${new Date().toISOString().slice(0, 10)}.tsv`, values, idempotencyKey);
      assertCurrent();
      if (!created.totalRows || created.validRows !== created.totalRows || created.failedRows > 0) {
        reload();
        assertBulkOperationValid(created, inputRows);
        throw new Error('입력 오류를 수정한 뒤 다시 검증해 주세요.');
      }
      const submitted = fixtureMode ? { ...created, status: 'QUEUED', submittedAt: new Date().toISOString() }
        : await port.submitBulkOperation(created.id, `${idempotencyKey}:submit`);
      assertCurrent();
      if (fixtureMode) saveFixtureBulkOperation({ ...created, ...submitted });
      route.openJob(created.id);
      reload();
    } finally { applying.current = false; }
  };
  const collectionState: ResourceCollectionState = resourceLoading && !resourceData ? { kind: 'loading' }
    : resourceError ? { kind: 'error', message: resourceError, onRetry: reload }
      : rows.length ? { kind: 'ready' } : { kind: 'empty', title: sourceJobs.length ? '검색 조건에 맞는 처리 내역이 없습니다.' : '등록된 처리 내역이 없습니다.' };

  return <>
    <ResourcePage title={location.source === 'people' ? '구성원 처리 내역' : location.source === 'staffing' ? '인사 발령 처리 내역' : '대량 작업'}
      refreshAction={{ onClick: reload, loading: resourceLoading }}
      navigation={location.source && route.returnToSource ? <Button startIcon={<ArrowBackOutlined />} onClick={route.returnToSource}>{location.source === 'people' ? '구성원으로 돌아가기' : '인사 발령으로 돌아가기'}</Button> : undefined}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResourceCollection state={collectionState} rows={rows} totalCount={sourceJobs.length} columns={columns}
          query={query} onQueryChange={setQuery} searchPlaceholder="작업 또는 입력 검색" ariaLabel="대량 작업 목록"
          filters={[{ label: '상태', value: status, options: [{ label: '전체', value: '' }, { label: '검증 완료', value: 'VALIDATED' }, { label: '적용 대기', value: 'QUEUED' }, { label: '처리 중', value: 'PROCESSING' }, { label: '완료', value: 'COMPLETED' }, { label: '일부 실패', value: 'COMPLETED_WITH_ERRORS' }], onChange: setStatus }]}
          onRowClick={({ row }) => route.openJob(row.id)} />
      </Box>
    </ResourcePage>
    {location.active && location.jobId && !dialogOpen && <BulkOperationDetailDialog key={`${location.jobId}:${fixtureMode}`} jobId={location.jobId} port={port} route={route} fixtureMode={fixtureMode}
      canCreate={canCreate} canRun={canRun} canReadStaffing={canReadStaffing} allowedOperationTypes={allowedOperationTypes} />}
    {dialogWorkflows.length > 0 && <BulkPasteDialog open={dialogOpen} workflows={dialogWorkflows} referencePort={references} initialOperationType={requestedOperation} onClose={route.clearOperation} onApply={apply} />}
  </>;
}
